/**
 * Bundle 155 - event countdown / past-event formatter.
 *
 * Single source of truth for how the public site talks about
 * event timing. Handles three cases:
 *
 *   * future  -> "Starts in 3 days" / "Starts in 4h 12m"
 *   * present -> "Happening now" (within a 15 minute grace band
 *                around the start time so a last-minute jitter
 *                does not flip copy back and forth)
 *   * past    -> "Ended just now" / "Ended 3 days ago" /
 *                "Ended in July 2026" (>=180 days)
 *
 * Also exports `isPastEvent(event)` mirroring the backend
 * predicate in services/past_event_service.py so client-side
 * list rendering does not have to fetch the detail endpoint.
 */

const _parseIso = (v) => {
  if (!v) return null;
  const d = v instanceof Date ? v : new Date(v);
  return Number.isFinite(d.getTime()) ? d : null;
};

export const isPastEvent = (event) => {
  if (!event) return false;
  const now = Date.now();
  for (const k of ['end_at', 'end_datetime', 'end_date']) {
    const d = _parseIso(event[k]);
    if (d) return d.getTime() < now;
  }
  for (const k of ['event_date', 'start_datetime', 'start_date']) {
    const d = _parseIso(event[k]);
    if (d) return d.getTime() < now;
  }
  return false;
};

/**
 * Format the countdown / past-event copy.
 *
 * @param {string | Date} startDate - canonical event start.
 * @param {object} [opts]
 * @param {string | Date} [opts.endDate] - if provided, the past
 *   check uses `endDate` first (matches backend precedence).
 * @param {Date} [opts.now] - inject a Date for tests.
 * @returns {{state: 'future'|'present'|'past', label: string}}
 */
export const formatEventCountdown = (startDate, opts = {}) => {
  const start = _parseIso(startDate);
  const end = _parseIso(opts.endDate);
  const now = opts.now instanceof Date ? opts.now : new Date();
  if (!start && !end) {
    return { state: 'future', label: '' };
  }
  const nowMs = now.getTime();
  // "Past" reference point: end_date if available, else start_date.
  const pastRef = end || start;
  if (pastRef && pastRef.getTime() < nowMs) {
    return { state: 'past', label: _formatPast(pastRef, now) };
  }
  // "Present" grace band: within 15 minutes on either side of
  // start OR between start and end.
  if (start) {
    const startMs = start.getTime();
    const graceMs = 15 * 60 * 1000;
    const inGrace = Math.abs(startMs - nowMs) <= graceMs;
    const between = end && startMs <= nowMs && nowMs <= end.getTime();
    if (inGrace || between) {
      return { state: 'present', label: 'Happening now' };
    }
    if (startMs > nowMs) {
      return { state: 'future', label: _formatFuture(start, now) };
    }
  }
  return { state: 'past', label: 'Past event' };
};

const _formatFuture = (start, now) => {
  const diff = start.getTime() - now.getTime();
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  if (days >= 1) return `Starts in ${days} day${days === 1 ? '' : 's'}`;
  if (hours >= 1) return `Starts in ${hours}h ${mins}m`;
  return `Starts in ${Math.max(mins, 0)}m`;
};

const _formatPast = (ref, now) => {
  const diff = now.getTime() - ref.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 5) return 'Ended just now';
  if (mins < 60) return `Ended ${mins} minutes ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `Ended ${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `Ended ${days} day${days === 1 ? '' : 's'} ago`;
  if (days < 14) return 'Ended last week';
  if (days < 30) {
    const weeks = Math.floor(days / 7);
    return `Ended ${weeks} weeks ago`;
  }
  if (days < 60) return 'Ended last month';
  if (days < 180) {
    const months = Math.floor(days / 30);
    return `Ended ${months} months ago`;
  }
  // 180+ days: absolute month + year to avoid awkward "9 months ago" that
  // stops being informative.
  const month = ref.toLocaleString('en-US', { month: 'long' });
  const year = ref.getFullYear();
  return `Ended in ${month} ${year}`;
};
